/**
 * LiveTicksContext — single shared WebSocket connection for the whole app.
 *
 * Wraps useLiveTicks() once at the AppShell level so every component reads
 * from the same connection instead of opening its own.
 *
 * Usage (drop-in replacement for the old hook import):
 *   import { useLiveTicks } from "@/contexts/LiveTicksContext";
 */
import { createContext, useContext, type ReactNode } from "react";
import { useLiveTicks as _useLiveTicks } from "@/hooks/useLiveTicks";

type LiveTicksValue = ReturnType<typeof _useLiveTicks>;

const LiveTicksContext = createContext<LiveTicksValue | null>(null);

export function LiveTicksProvider({ children }: { children: ReactNode }) {
  const ticks = _useLiveTicks();
  return (
    <LiveTicksContext.Provider value={ticks}>
      {children}
    </LiveTicksContext.Provider>
  );
}

export function useLiveTicks(): LiveTicksValue {
  const ctx = useContext(LiveTicksContext);
  if (!ctx) throw new Error("useLiveTicks must be used inside <LiveTicksProvider>");
  return ctx;
}

// Re-export types so components can keep a single import path
export type { TickEntry, AutoCloseEvent, ScanCompleteEvent, ScanStats, ScanRegime, ScanDrawdown, StockTone, BnToneEvent, PortfolioPnlEvent, SystemAlertEvent, IndicesStatusEvent, AppStatusEvent, ScanMonitorEvent } from "@/hooks/useLiveTicks";
